import {useEffect, useRef} from "react";
import {useQueryClient} from "@tanstack/react-query";

import {Dialog, DialogContent, DialogTitle} from "@/components/ui/dialog";
import {ComplianceReconcileOverlay} from "@/src/modules/core/components/compliance-reconcile-overlay";
import {ComplianceStickyBar} from "@/src/modules/core/components/compliance-sticky-bar";
import {useComplianceDeferred} from "@/src/modules/core/hooks/use-compliance-defer";
import {selectActiveGateId} from "@/src/modules/core/lib/compliance-gate-priority";
import {COMPLIANCE_STICKY_BAR_DATA_ATTR} from "@/src/modules/core/lib/compliance-sticky-bar-layout";
import {reconcileCompliance} from "@/src/modules/core/lib/reconcile-compliance";
import {useComplianceBusyStore} from "@/src/modules/core/store/compliance-busy";
import {
  useBlockingGateQueue,
  useBlockingGateReconciling,
  useBlockingGateStore,
  useSetBlockingGateReconciling,
} from "@/src/modules/core/store/blocking-gate";

export function BlockingGateRenderer() {
  const queue = useBlockingGateQueue();
  const isDeferred = useComplianceDeferred();
  const reconciling = useBlockingGateReconciling();
  const setReconciling = useSetBlockingGateReconciling();
  const queryClient = useQueryClient();

  useEffect(() => {
    if (queue.length === 0) {
      useComplianceBusyStore.getState().clearUserEscalation();
    }
  }, [queue.length]);

  const wasDeferredRef = useRef(isDeferred);
  useEffect(() => {
    const wasDeferred = wasDeferredRef.current;
    wasDeferredRef.current = isDeferred;
    if (!wasDeferred || isDeferred) {
      return;
    }

    const activeGateId = selectActiveGateId(useBlockingGateStore.getState().gates);
    if (activeGateId == null) {
      return;
    }

    setReconciling(true, "system");
    void reconcileCompliance(queryClient).finally(() => {
      setReconciling(false, "system");
    });
  }, [isDeferred, queryClient, setReconciling]);

  const showFallbackOverlay = reconciling && !isDeferred && queue.length === 0;
  const showStickyBar = isDeferred && queue.length > 0;

  useEffect(() => {
    if (showStickyBar) {
      document.documentElement.setAttribute(COMPLIANCE_STICKY_BAR_DATA_ATTR, "");
    } else {
      document.documentElement.removeAttribute(COMPLIANCE_STICKY_BAR_DATA_ATTR);
    }
  }, [showStickyBar]);

  return (
    <>
      {showFallbackOverlay ? (
        <Dialog open onOpenChange={() => {}}>
          <DialogContent showCloseButton={false} className="sm:max-w-xs">
            <DialogTitle className="sr-only">Checking</DialogTitle>
            <div className="py-6" aria-hidden />
            <ComplianceReconcileOverlay />
          </DialogContent>
        </Dialog>
      ) : null}

      {showStickyBar ? <ComplianceStickyBar queue={queue} /> : null}
    </>
  );
}
